// src/stage2/sceneExterior/Bike.jsx
import React, { useMemo, useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { useGLTF } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";

const BIKE_URL = "/stageAssets/stage2/scene/Stage2Exterior-BIKE-blender.glb";
const tmpVec = new THREE.Vector3();

export function Bike({
  bikeId,
  color = "#2ecc71",
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,

  bikesLiveRef,         // (live store from Stage2)
  activeBikeId,         // optional (not required)
  onMountRequest,
  onNearChange,
  nearDistance = 1.5,
  disabled = false,
}) {
  const { scene } = useGLTF(BIKE_URL);
  const { camera } = useThree();

  const rootRef = useRef();
  const baseYRef = useRef(position[1] ?? 0);
  const wasNearRef = useRef(false);

  const [isNear, setIsNear] = useState(false);

  const bike = useMemo(() => (scene ? scene.clone(true) : null), [scene]);

  const glowMatsRef = useRef([]);

  useEffect(() => {
    if (!bike) return;

    glowMatsRef.current = [];

    bike.traverse((obj) => {
      if (!obj.isMesh || !obj.material) return;

      obj.material = obj.material.clone();

      if (obj.material.name === "main") {
        obj.material.color = new THREE.Color(color);
      }

      if ("emissive" in obj.material) {
        obj.material.emissive = new THREE.Color("#000000");
        obj.material.emissiveIntensity = 0;
        glowMatsRef.current.push(obj.material);
      }

      obj.castShadow = true;
      obj.receiveShadow = true;
    });
  }, [bike, color]);

  // set initial position once
  useEffect(() => {
    if (!rootRef.current) return;
    rootRef.current.position.set(position[0], position[1], position[2]);
    rootRef.current.rotation.set(rotation[0], rotation[1], rotation[2]);
    rootRef.current.scale.setScalar(scale);
  }, [position, rotation, scale]);

  useFrame((state) => {
    if (!rootRef.current) return;

    // ✅ 1) If we have live data for this bike, apply it every frame
    const live = bikesLiveRef?.current?.[bikeId];
    if (live) {
      rootRef.current.position.copy(live.pos);
      rootRef.current.rotation.y = live.yaw;

      // keep baseY in sync so when we bob near, it bobs from the current height
      baseYRef.current = live.pos.y;
    }

    // ✅ 2) Proximity check (use world pos after applying live transform)
    if (disabled) return;

    const worldPos = rootRef.current.getWorldPosition(tmpVec);
    const dist = camera.position.distanceTo(worldPos);
    const nearNow = dist < nearDistance;

    if (nearNow !== wasNearRef.current) {
      wasNearRef.current = nearNow;
      setIsNear(nearNow);
      onNearChange?.(bikeId, nearNow);
    }

    // ✅ 3) Visual cue ONLY when near AND not currently being ridden
    // If you want glow even while riding, remove this guard.
    const isRidden = !!activeBikeId && activeBikeId === bikeId;

    const t = state.clock.getElapsedTime();
    if (nearNow && !isRidden) {
      // bob
      rootRef.current.position.y = baseYRef.current + Math.sin(t * 6) * 0.03;

      // pulse
      const s = 1 + Math.sin(t * 8) * 0.015;
      rootRef.current.scale.setScalar(scale * s);

      // glow
      glowMatsRef.current.forEach((m) => {
        m.emissive.set("#ffe48a");
        m.emissiveIntensity = 0.55;
      });
    } else {
      // reset visuals (but do NOT overwrite live position)
      rootRef.current.scale.setScalar(scale);
      glowMatsRef.current.forEach((m) => {
        m.emissive.set("#000000");
        m.emissiveIntensity = 0;
      });
    }
  });

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (disabled) return;
      if (!isNear) return;
      onMountRequest?.(bikeId);
    },
    [bikeId, isNear, onMountRequest, disabled]
  );

  if (!bike) return null;

  return (
    <group
      ref={rootRef}
      onClick={handleClick}
      onPointerOver={() => (document.body.style.cursor = disabled ? "default" : "pointer")}
      onPointerOut={() => (document.body.style.cursor = "default")}
    >
      <primitive object={bike} />
    </group>
  );
}

useGLTF.preload(BIKE_URL);
